import type { ProductConfig } from './product/product.types';
import type { InstallmentCalculatorConfig } from './product/installmentCalculator.types';
import type { CustomerRatingsConfig } from './product/customerRatings.types';
import type { OrderConfig } from './order/order.types';
import type { DynamicHelpConfig } from './cms/dynamicHelp.types';
import type { PaymentMethodsConfig } from './payment';

export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends Array<infer U>
    ? Array<DeepPartial<U>>
    : T[P] extends ReadonlyArray<infer U>
      ? ReadonlyArray<DeepPartial<U>>
      : DeepPartial<T[P]>;
};

export type CookieCategory = 'C0001' | 'C0003' | 'C0002' | 'C0004' | 'C0005' | 'C0006';

export type GeoCountryCode = string | undefined;
export type Flags = 'de' | 'ch' | 'fr' | 'at' | 'it' | 'nl';
export type Language = 'de' | 'fr' | 'en' | 'nl';
export enum LocaleEnum {
  'de-DE' = 'de-DE',
  'fr-CH' = 'fr-CH',
  'de-CH' = 'de-CH',
  'de-AT' = 'de-AT',
  'nl-NL' = 'nl-NL',
}
export type Locale = keyof typeof LocaleEnum;

export type PreRenderAtBuldTimeVariants = 'type' | 'cmsPage';

export enum Environment {
  STAGING = 'staging',
  PRODUCTION = 'production',
}

export type Config = PaymentMethodsConfig & {
  host: {
    name: string;
    domain: string;
    appDeeplinkProtocol?: string;
  };
  i18n: {
    defaultLocale: Locale;
    languages: Language[];
    locales: Locale[];
  };
  clientId: string;
  tenantId: string;
  styleVariant: 'default' | 'dark';
  company: {
    name: string;
    seoTitleName: string;
  };
  timeZone: string;
  captcha: {
    recaptchaSessionToken: string;
  };
  assets: {
    cmsImage: {
      imageQuality: number;
      imageOptimizationEnabled: boolean;
      uriTransformSource: RegExp;
      uriTransformEndpoint: string;
    };
    assetServerHost: string;
    fallbackImageUrl: string;
  };
  middleware: {
    cmsRewrites: {
      exact: string[];
      prefixes: string[];
    };
  };
  apiEndpoints: {
    search: string;
    searchFilter: string;
    searchSeo: string;
    suggest: string;
    historyGet: string;
    historyApiNew: string;
    historyDelete: string;
    historyDeleteAll: string;
    historyDeleteAllNew: string;
    historyJoinWithFi: string;
    customer: string;
  };
  insiderPushNotifications: {
    enabled: boolean;
    apiKey?: string;
    partnerName?: string;
    partnerId?: number;
  };
  dynamicYieldEnabled: boolean;
  hermesShopFinder: HermesShopFinderConfig;
  product: ProductConfig;
  installmentCalculator: InstallmentCalculatorConfig;
  header: HeaderConfig;
  usp: UspConfig;
  search: SearchConfig;
  order: OrderConfig;
  staticContent: StaticContentConfig;
  footer: FooterConfig;
  banner: BannerConfig;
  previewApp: PreviewAppConfig;
  nlSheetTexts: NlSheetTextsConfig;
  serviceLayer: ServiceLayerConfig;
  seoContent: SeoContentConfig;
  survey: SurveyConfig;
  sovendus: {
    enabled: boolean;
  };
  chatBot: ChatBotConfig;
  promotionBanner: PromotionBannerConfig;
  voucher: VoucherConfig;
  recommendations: RecommendationsConfig;
  searchResultUserFeedback: FeedbackConfig;
  shoppingApp: {
    pathsToRenderChildrenOnly: string[];
  };
  navigation: {
    firstLevel: {
      color: string;
      backgroundColor?: string;
      border: string;
    };
    secondLevel?: {
      color: string;
      backgroundColor?: string;
      border: string;
    };
    loadSliced: boolean;
    firstLevelFont?: string;
    firstLevelFontSize?: string;
    dataBucket: string;
  };
  forms: {
    useOptimizely: boolean;
    newsletterSettings: {
      enabled: boolean;
      environmentRelated: boolean;
      useDOI: boolean;
      sendAdditionalSubscribe: boolean;
      useApproval: boolean;
    };
    contact: {
      dynamicHelp: DynamicHelpConfig;
    };
    newsletterUnsubscribe: {
      fields: CmsFormsFieldConfig[];
      useDedicatedSubmitButtonTitleFor?: string[];
    };
    newsletterSubscribe: Record<Language, { unsubscribeLink: string; voucherLink: string }>;
    braFinder: Record<Language, { searchLink: string }>;
    newsletterSheet: NewsletterSheetConfig;
    validation: {
      rules: {
        customerId: {
          regex: RegExp;
          errorMessage: {
            id: string;
            defaultMessage: string;
          };
        };
        postalCode: {
          regex: RegExp;
          errorMessage: {
            id: string;
            defaultMessage: string;
          };
        };
        fileUpload: {
          fileLimit: number;
          maxFileSizeMb: number;
          maxFileSizeSummaryMb: number;
          acceptedFormats: string;
          regexToCheckImageFormats: RegExp;
          regexAcceptedUploadFormats: RegExp;
          indicators: {
            showFileSize: boolean;
            showFileSizeSummary: boolean;
          };
        };
      };
    };
  };
  enableCustomerNrLogin: boolean;
  headerSlots: {
    localeSwitcher: boolean;
    payback: boolean;
    joe: boolean;
    serviceApi?: string;
  };
  headerLinks: Record<
    Locale,
    {
      serviceLink: string;
    }
  >;
  appBanner: {
    enabled: boolean;
    appUrl: string;
  };
  tracking: TrackingConfig;
  abTesting: AbTestingConfig;
  testPanel: TestPanelConfiguration;
  externalScript?: {
    kuecheCo?: {
      key: string;
    };
  };
  video: {
    video_youtube: { url: string; provider: string; name: string };
  };
  headerInformation: Partial<
    Record<
      Language,
      {
        description: string;
        title: string;
        canonical: string;
        robots: string;
      }
    >
  >;
  customerRatings: CustomerRatingsConfig;
  customerCentricFeedback: {
    enabled: boolean;
    tenantId: string;
    surveyIdStorefront: string;
    surveyIdSearch: string;
    surveyIdDetailview: string;
    surveyIdWishlist: string;
    surveyIdMyAccount: string;
  };
  dynamicYieldTracking: {
    key: string;
    endpoint: string;
    endpointClientSide: string;
  };
  officeTimes: Partial<Record<'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun', string>>;
  geoCountryLayer: {
    enabled: boolean;
    flag?: Flags;
    img: {
      path: string;
      alt: string;
      title: string;
    };
    alternatives?: AlternativesShops[];
  };
  paybackCoupons: {
    img: {
      path?: string;
    };
  };

  lounge: {
    icon?: (lang: string) => string;
  };
  bookings: {
    overview: {
      hasExplicitMaturities: boolean;
      showOpenPayment: 'none' | 'minimal-payment' | 'installments' | 'sum-and-open';
    };
  };
  priceFormat: PriceFormat;
  speedKit: {
    enabled: boolean;
    scriptUrl?: string;
  };
  // This configuration is used for tenants that are in the process of closing and require certain UI elements to be hidden.
  // Specifically, it allows hiding the search field, navigation, and the basket, wishlist, and search slots.
  isTenantClosing: boolean;
};

export type InternalPageMetadata = {
  title?: string;
};

// INSPIRE-4899 - this type is based on the schema.org Organization type,
// but only contains the fields we actually use in the app.
// It is typed as a record with locale keys to allow for localized shop information.
export type SchemaOrgOrganisation = Record<
  Locale,
  {
    '@context': 'https://schema.org';
    '@type': 'Organization';
    '@id': string;
    name: string;
    alternateName: string;
    description: string;
    url: string;
    logo: string;
    foundingDate: string;
    founder: {
      '@type': 'Person';
      name: string;
      description: string;
      sameAs: string;
    };
    location: {
      '@type': 'Place';
      name: string;
      address: {
        '@type': 'PostalAddress';
        streetAddress: string;
        addressLocality: string;
        addressRegion: string;
        postalCode: string;
        addressCountry: string;
      };
    };
    contactPoint: [
      {
        '@type': 'ContactPoint';
        contactType: 'customer service';
        telephone: string;
        email: string;
        availableLanguage: string[];
        hoursAvailable: string[];
        url: string;
      },
    ];
    sameAs: string[];
    parentOrganization?: {
      '@type': 'Organization';
      name: 'Otto Group';
      sameAs: 'https://www.ottogroup.com/';
    };
    memberOf?: {
      '@type': 'Organization';
      name: 'Otto Group';
      sameAs: 'https://www.ottogroup.com/';
    };
    legalName: string;
    areaServed: string;
    foundingLocation: string;
    slogan: string;
  }
>;

export type AlternativesShops = {
  flag?: Flags;
  shops?: AlternativesShopsItem[];
};

export type AlternativesShopsItem = {
  link?: string;
  title?: string;
  closeLayer?: boolean;
};

export interface TrackingConfig {
  categoryIdHomepage: string;
  soastaId: string;
  clarityIdStaging?: string;
  clarityIdProduction?: string;
  tagmanagerId: string;
  tagmanagerDomain?: string;
  /** Full script url. Overwrites tagmanagerId and tagmanagerDomain config */
  tagmanagerSrc?: string;
  googleSiteVerificationID?: string[];
  msvalidateID?: string;
  schemaOrgOrganisation: SchemaOrgOrganisation;
  pinterestRichPinVerificationID?: string;
}

export interface AbTestingConfig {
  serverSideEnrollment: {
    /**
     * List of all active test ids all users should be automatically enrolled in on the server-side.
     *
     * Enrollment on the server side has the benefits that it can be used for data fetching even on the first request, but the drawbacks that it will delay the first response on a new session/visit to fetch the info.
     */
    activeTestIds: string[];
  };
  clientSideEnrollment: {
    /**
     * List of all active test ids all users should be automatically enrolled in on the client-side.
     *
     * Enrollment on the client side has the benefits that it will not delay the first response on a new session/visit, but the drawbacks that it can not be used for data fetching on the first request.
     */
    activeTestIds: string[];
  };
  /**
   * Test ID for the pRank A/B test (MBA product ranking).
   * Only defined for tenants participating in the test (baurde, universalat).
   */
  pRankTestId?: string;
  /**
   * Test ID for the Odoscope Ranked A/B test.
   * Only defined for tenants participating in the test (baurde).
   */
  odoscopeRankedTestId?: string;
}

export interface TestPanelConfiguration {
  apps?: TestPanelApp[];
  backends?: TestPanelApp[];
  systems?: TestPanelSystem[];
}

export interface TestPanelApp {
  appLabel: string;
  appName: string;
  defaultBranch: string;
  projectId: string;
  featureCookieName: string;
  hasPathQuerySyntax?: boolean;
}

export interface TestPanelSystem {
  appLabel: string;
  appName: string;
  connector: 'apiKeyManager';
  systems: Record<string, string>;
  featureCookieName?: string;
  defaultBranch?: string;
}

export interface ExternalService {
  apiKey?: string;
  apiUrl?: string;
  consentGroup?: CookieCategory;
  containerName?: string;
  customerId?: string;
  debugEnabled?: boolean;
  enabled?: boolean;
  moreInformationLink?: string;
  scriptUrl?: string;
}

export type PersonalizationSegmentSource = 'legacy' | 'baur' | 'unito';

export type AdServerDisabledConfig = {
  mode: 'disabled';
  mkzBlacklist: number[];
};

export type AdServerContainerOnlyConfig = {
  mode: 'containerOnly';
  mkzBlacklist: number[];
};

export type AdSlotConfig = {
  /**
   * DoubleClick ad unit name
   */
  adUnitName: string;
  /**
   * Ad dimensions
   */
  adSizes: googletag.GeneralSize[];
};

export type AdSlotsConfig = {
  /**
   * Sidebar skyscraper ad ([[160x600]])
   */
  skyscraper?: AdSlotConfig;
  /**
   * Product list banner ad (['fluid', [884, 250]])
   */
  banner?: AdSlotConfig;
};

export type AdServerDoubleClickConfig = {
  mode: 'doubleClick';
  adServerClientId: string;
  mkzBlacklist: number[];
  /**
   * Object with named ad slots
   */
  slots: AdSlotsConfig;
};

export type AdServerConfig =
  | AdServerDisabledConfig
  | AdServerContainerOnlyConfig
  | AdServerDoubleClickConfig;

export type SearchConfig = {
  personalizationSegmentSource: PersonalizationSegmentSource;
  adServer: AdServerConfig;
  hideTelekomIcon: boolean;
  /** Odoscope tenant token - cookie will be named osc-<TOKEN> */
  odoscopeToken?: string;
};

export type StaticContentConfig = {
  fetchSettings: {
    timeout: number;
  };
  apiEndpoints: {
    jsonApi: string;
    bucket: string;
    webappJson: string;
  };
  ssg: {
    revalidateInterval: number;
  };
};

export type HeaderConfig = {
  hasSmallerFontSize: boolean;
  desktopNavigationMode: 'click' | 'hover';
};

export type UspConfig = {
  isEnabled: boolean;
};

export type FooterConfig = {
  fetchSettings: {
    timeout: number;
  };
  apiEndpoints: {
    jsonApi: string;
    bucket: string;
    deDupingInterval: number;
  };
  ssg: {
    revalidateInterval: number;
  };
};

export type RecommendationsConfig = {
  apiEndpoint: string;
  provider: 'prudsys' | 'empiriecom';
};

export type FeedbackConfig = {
  appUrl?: string;
  enabled: boolean;
};

export type BannerConfig = {
  apiEndpoints: {
    bucket: string;
  };
  ssg: {
    revalidateInterval: number;
  };
};

export type PreviewAppConfig = {
  drupalColors: {
    backgroundDark: string;
    primary: string;
  };
};

export type NlSheetTextsConfig = {
  apiEndpoints: {
    bucket: string;
  };
  ssg: {
    revalidateInterval: number;
  };
};

export type ServiceLayerConfig = {
  apiEndpoints: {
    api: string;
    bucket: string;
  };
  ssg: {
    revalidateInterval: number;
  };
};

export type SurveyConfig = {
  apiEndpoints: {
    bucket: string;
  };
};

export type HermesShopFinderConfig = {
  url: string;
  rParam: string;
};

export type NewsletterSheetConfig = {
  enabled: boolean;
};

export type ChatBotConfig = {
  enabled: boolean;
  key: string;
  includePaths: string[];
  excludePaths: string[];
  visibility: {
    main: boolean;
    button: boolean;
    notifications: boolean;
  };
};

export type SeoContentConfig = {
  apiEndpoints: {
    bucket: string;
  };
  url: Record<Locale, { privacyNoticePath: string; potentialActionTarget: string }>;
};

export type PromotionBannerConfig = {
  apiEndpoints: {
    jsonApi: string;
    bucketPromotion: string;
    bucketEmptyPromotions: string;
    bucketContentSnippet: string;
  };
  abTest?: string;
};

export type CmsFormsFieldOptionsConfig = {
  id: string;
  value: string;
  messageKey: string;
};

export type CmsFormsFieldConfig = {
  id: string;
  type?: string;
  defaultValue?: string;
  options?: CmsFormsFieldOptionsConfig[];
  formType?: 'input' | 'radio' | 'select' | 'text' | 'email' | 'number' | 'password' | 'date';
  messageKeyPlaceholder?: string;
  additionalForm?: CmsFormsFieldConfig;
  showOnlyWhenWhatSelected?: string;
};

export type VoucherConfig = {
  apiEndpoints: {
    api: string;
    bucket: string;
  };
};

export type LocalizedPath = Partial<Record<Locale, string>> & {
  default: string;
};

export type PriceFormat = 'Empiriecom' | 'NoDelimiterCurrencyAtTheEnd' | 'Iso';
